















































































































































import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { Location } from 'vue-router';

import { AccountModule } from '@/store/modules/account';
import {
  CancelScCatalogFavorite,
  CreateScCatalogFavorite,
  GetScCatalogPage,
  ScCatalogItem,
  ScSkuCatalogItem,
  ScSyncProductCatalogItem,
} from '../api/sc/index';
import { GetWebSkuCategoryOptions, SKU_CATEGORY_TYPE, SkuCategoryTreeItem } from '../api/sku-category';
import MicroSprayProductCard from '../components/micro-spray-product-card.vue';

const SC_CATALOG_SCROLL_KEY = 'sc_catalog_return_state';
const SC_CATALOG_SCROLL_TTL = 10 * 60 * 1000;

type ScCatalogScrollState = {
  routeFullPath: string;
  scrollY: number;
  savedAt: number;
};

function normalizeArray<T>(value: T[] | null | undefined): T[] {
  return Array.isArray(value) ? value : [];
}

function readRouteQueryValue(value: string | string[] | undefined): string {
  return Array.isArray(value) ? value[0] || '' : value || '';
}

function readPositiveInteger(value: string | string[] | undefined, fallback = 1): number {
  const normalized = Number(readRouteQueryValue(value));
  return Number.isInteger(normalized) && normalized > 0 ? normalized : fallback;
}

function normalizeExternalUrl(value: string | undefined): string {
  const url = (value || '').trim();
  if (!url) {
    return '';
  }
  if (/^https?:\/\//i.test(url)) {
    return url;
  }
  if (/^\/\//.test(url)) {
    return `https:${url}`;
  }
  if (/^[a-z][a-z0-9+.-]*:/i.test(url)) {
    return '';
  }
  return `https://${url}`;
}

@Component({
  components: {
    MicroSprayProductCard,
  },
})
export default class SupplyChainCategory extends Vue {
  loading = false;
  loadError = false;
  categoryLoading = false;
  categoryLoadError = false;
  firstCategoryCode = '';
  secondCategoryCode = '';
  categoryTitle = '';
  pageNum = 1;
  pageSize = 12;
  total = 0;
  categoryTree: SkuCategoryTreeItem[] = [];
  catalogList: ScCatalogItem[] = [];
  chatQrCodeUrl = 'https://cdn-h.gongfudou.com/Epson/wechat_qrcoder.png';
  favoriteLoadingKey = '';
  requestId = 0;

  get secondCategoryOptions() {
    const firstCategory = this.categoryTree.find(item => item.code === this.firstCategoryCode);
    return normalizeArray<SkuCategoryTreeItem>(firstCategory && firstCategory.children).filter(item => item.isEnabled !== false);
  }

  get emptyText() {
    if (!this.firstCategoryCode) {
      return '缺少品类参数，请返回生态供应链重新选择。';
    }
    return this.secondCategoryCode ? '当前二级分类暂无已审核通过商品。' : '当前一级分类暂无已审核通过商品。';
  }

  @Watch('$route.query', { immediate: true, deep: true })
  async onRouteQueryChange() {
    if (!this.ensureLogin()) {
      return;
    }
    this.syncRouteQuery();
    if (!this.isCurrentQueryCanonical()) {
      await this.replaceCatalogQuery({});
      return;
    }
    const currentRequestId = ++this.requestId;
    await this.ensureCategoryOptions();
    if (currentRequestId !== this.requestId) {
      return;
    }
    if (
      this.secondCategoryCode &&
      this.categoryTree.length &&
      !this.secondCategoryOptions.some(item => item.code === this.secondCategoryCode)
    ) {
      await this.replaceCatalogQuery({ secondCategoryCode: '', pageNum: 1 });
      return;
    }
    await this.fetchCatalog(currentRequestId);
  }

  ensureLogin() {
    if (AccountModule.isLogined) {
      return true;
    }
    this.$message.warning('请先登录后查看商品列表');
    this.$router.replace({
      path: '/login',
      query: {
        redirect: this.$route.fullPath,
      },
    });
    return false;
  }

  syncRouteQuery() {
    this.firstCategoryCode = readRouteQueryValue(this.$route.query.firstCategoryCode as string | string[] | undefined);
    this.secondCategoryCode = readRouteQueryValue(this.$route.query.secondCategoryCode as string | string[] | undefined);
    this.categoryTitle = readRouteQueryValue(this.$route.query.categoryTitle as string | string[] | undefined);
    this.pageNum = readPositiveInteger(this.$route.query.pageNum as string | string[] | undefined);
  }

  isCurrentQueryCanonical() {
    const resolved = this.$router.resolve({
      path: '/supplyChainCategory',
      query: this.buildCatalogQuery(),
    });
    return resolved.route.fullPath === this.$route.fullPath;
  }

  async ensureCategoryOptions(force = false) {
    if (!force && this.categoryTree.length) {
      return;
    }
    this.categoryLoading = true;
    this.categoryLoadError = false;
    try {
      const res = await GetWebSkuCategoryOptions(SKU_CATEGORY_TYPE.JD_ECO);
      this.categoryTree = normalizeArray<SkuCategoryTreeItem>(res);
    } catch (error) {
      this.categoryTree = [];
      this.categoryLoadError = true;
    } finally {
      this.categoryLoading = false;
    }
  }

  async reloadCategoryOptions() {
    await this.ensureCategoryOptions(true);
    if (
      !this.categoryLoadError &&
      this.secondCategoryCode &&
      !this.secondCategoryOptions.some(item => item.code === this.secondCategoryCode)
    ) {
      await this.replaceCatalogQuery({ secondCategoryCode: '', pageNum: 1 });
    }
  }

  async fetchCatalog(currentRequestId = ++this.requestId) {
    if (!this.firstCategoryCode) {
      this.catalogList = [];
      this.total = 0;
      this.loading = false;
      this.loadError = false;
      return;
    }
    this.loading = true;
    this.loadError = false;
    try {
      const requestedPage = this.pageNum;
      const res = await GetScCatalogPage({
        firstCategoryCode: this.firstCategoryCode,
        secondCategoryCode: this.secondCategoryCode || undefined,
        pageNum: requestedPage,
        pageSize: this.pageSize,
      });
      if (currentRequestId !== this.requestId) {
        return;
      }
      const responsePage = readPositiveInteger(String((res && res.current) || requestedPage));
      this.pageSize = readPositiveInteger(String((res && res.size) || this.pageSize), this.pageSize);
      this.total = Math.max(Number((res && res.total) || 0), 0);
      this.catalogList = normalizeArray<ScCatalogItem>(res && res.records).filter(
        item => item && (item.itemType === 'SC_SKU' || item.itemType === 'SC_SYNC_PRODUCT') && Boolean(item.targetSn)
      );
      if (responsePage !== requestedPage) {
        await this.replaceCatalogQuery({ pageNum: responsePage });
        return;
      }
      this.pageNum = responsePage;
      this.restoreScrollPosition();
    } catch (error) {
      if (currentRequestId !== this.requestId) {
        return;
      }
      this.catalogList = [];
      this.total = 0;
      this.loadError = true;
    } finally {
      if (currentRequestId === this.requestId) {
        this.loading = false;
      }
    }
  }

  retryCatalog() {
    this.fetchCatalog(++this.requestId);
  }

  buildCatalogQuery(overrides: { secondCategoryCode?: string; pageNum?: number } = {}) {
    const secondCategoryCode = overrides.secondCategoryCode !== undefined ? overrides.secondCategoryCode : this.secondCategoryCode;
    const pageNum = overrides.pageNum !== undefined ? overrides.pageNum : this.pageNum;
    const query: Record<string, string> = {
      firstCategoryCode: this.firstCategoryCode,
      pageNum: String(Math.max(pageNum, 1)),
    };
    if (secondCategoryCode) {
      query.secondCategoryCode = secondCategoryCode;
    }
    if (this.categoryTitle) {
      query.categoryTitle = this.categoryTitle;
    }
    return query;
  }

  async replaceCatalogQuery(overrides: { secondCategoryCode?: string; pageNum?: number }) {
    const location: Location = {
      path: '/supplyChainCategory',
      query: this.buildCatalogQuery(overrides),
    };
    try {
      await this.$router.replace(location);
    } catch (error) {
      return;
    }
  }

  handleSecondCategorySelect(code: string) {
    if (code === this.secondCategoryCode) {
      return;
    }
    this.replaceCatalogQuery({ secondCategoryCode: code, pageNum: 1 });
  }

  handlePageChange(page: number) {
    if (page === this.pageNum) {
      return;
    }
    this.replaceCatalogQuery({ pageNum: page });
  }

  buildItemKey(item: ScCatalogItem) {
    return `${item.itemType}:${item.targetSn}`;
  }

  async handleToggleFavorite(item: ScCatalogItem) {
    const itemKey = this.buildItemKey(item);
    if (!this.ensureLogin() || !item.targetSn || this.favoriteLoadingKey) {
      return;
    }
    this.favoriteLoadingKey = itemKey;
    try {
      const payload = {
        targetType: item.itemType,
        targetSn: item.targetSn,
      };
      if (item.favorited) {
        await CancelScCatalogFavorite(payload);
        item.favorited = false;
        this.$message.success('已取消收藏');
      } else {
        await CreateScCatalogFavorite(payload);
        item.favorited = true;
        this.$message.success('已收藏');
      }
    } finally {
      this.favoriteLoadingKey = '';
    }
  }

  handleSelectSyncProduct(item: ScSyncProductCatalogItem) {
    if (!item.targetSn) {
      return;
    }
    this.saveScrollPosition();
    const query: Record<string, string> = {
      productSn: item.targetSn,
      source: 'SUPPLY_CHAIN_CATEGORY',
      sourceFirstCategoryCode: this.firstCategoryCode,
      sourcePageNum: String(this.pageNum),
    };
    if (this.secondCategoryCode) {
      query.sourceSecondCategoryCode = this.secondCategoryCode;
    }
    if (this.categoryTitle) {
      query.sourceCategoryTitle = this.categoryTitle;
    }
    this.$router.push({
      path: '/sampleApply',
      query,
    });
  }

  saveScrollPosition() {
    const state: ScCatalogScrollState = {
      routeFullPath: this.$route.fullPath,
      scrollY: window.pageYOffset || document.documentElement.scrollTop || document.body.scrollTop || 0,
      savedAt: Date.now(),
    };
    try {
      window.sessionStorage.setItem(SC_CATALOG_SCROLL_KEY, JSON.stringify(state));
    } catch (error) {
      return;
    }
  }

  restoreScrollPosition() {
    try {
      const raw = window.sessionStorage.getItem(SC_CATALOG_SCROLL_KEY);
      if (!raw) {
        return;
      }
      const state = JSON.parse(raw) as ScCatalogScrollState;
      if (state.routeFullPath !== this.$route.fullPath || !state.savedAt || Date.now() - state.savedAt > SC_CATALOG_SCROLL_TTL) {
        window.sessionStorage.removeItem(SC_CATALOG_SCROLL_KEY);
        return;
      }
      window.sessionStorage.removeItem(SC_CATALOG_SCROLL_KEY);
      this.$nextTick(() => window.scrollTo(0, Math.max(Number(state.scrollY || 0), 0)));
    } catch (error) {
      window.sessionStorage.removeItem(SC_CATALOG_SCROLL_KEY);
    }
  }

  handleBack() {
    this.$router.push('/supplyChain');
  }

  hasSkuJdUrl(item: ScSkuCatalogItem) {
    return Boolean(normalizeExternalUrl(item.jdUrl));
  }

  handleViewSku(item: ScSkuCatalogItem) {
    const jdUrl = normalizeExternalUrl(item.jdUrl);
    if (jdUrl) {
      window.open(jdUrl, '_blank', 'noopener,noreferrer');
    }
  }

  displaySkuName(item: ScSkuCatalogItem) {
    return item.name || item.description || '未填写商品名称';
  }

  buildCategoryText(item: ScCatalogItem) {
    const values = [item.firstCategoryName || this.categoryTitle, item.secondCategoryName].filter(Boolean);
    return values.length ? values.join(' / ') : '未配置分类';
  }

  handleChatSupplier() {
    const qrCodeNode = this.$createElement('div', { class: 'supply-chain-chat-dialog' }, [
      this.$createElement('img', {
        class: 'supply-chain-chat-qrcode',
        attrs: {
          src: this.chatQrCodeUrl,
          alt: '微信二维码',
        },
      }),
    ]);
    this.$msgbox({
      title: '立即私聊',
      message: qrCodeNode,
      customClass: 'supply-chain-chat-message-box',
      confirmButtonText: '关闭',
      center: true,
    }).catch(() => undefined);
  }
}
